/**
 * InstallPrompt (task 5.3b)
 *
 * The app's own "Add to Home Screen" banner, shown in place of the
 * browser's mini-infobar (which `useInstallPrompt` suppresses). It renders
 * nothing at all until the trigger documented on that hook is met, so a
 * first-time visitor never sees it.
 *
 * Mounted once at the App level rather than per page: `beforeinstallprompt`
 * fires when the browser decides it should, not when a particular route
 * happens to be open, so the listener has to be alive for the whole session
 * or the event is simply missed.
 */

import React from 'react';
import { useAppMode } from '../../contexts/AppModeContext';
import { useCanvasData } from '../../contexts/CanvasDataContext';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import './InstallPrompt.css';

const InstallPrompt = () => {
  const { coursesMode } = useAppMode();
  const { courses } = useCanvasData();

  // The two halves of "this person has got something out of the app":
  // a real account (demo and guest are evaluation modes and are excluded by
  // name), and real data actually on screen rather than a spinner.
  const hasReceivedValue =
    coursesMode === 'auth' && Array.isArray(courses) && courses.length > 0;

  const { canPrompt, promptInstall, dismiss } = useInstallPrompt(hasReceivedValue);

  if (!canPrompt) return null;

  return (
    <div className="install-prompt" role="region" aria-label="Install Canvas Companion">
      <div className="install-prompt-body">
        <p className="install-prompt-title">Add Canvas Companion to your home screen</p>
        <p className="install-prompt-detail">
          Opens straight to the action plan, and keeps working when you have no signal.
        </p>
      </div>
      <div className="install-prompt-actions">
        <button type="button" className="install-prompt-install" onClick={promptInstall}>
          Install
        </button>
        <button type="button" className="install-prompt-dismiss" onClick={dismiss}>
          Not now
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
